"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.modelFactory = void 0;
const errors_1 = require("./errors");
const string_1 = require("./fields/string");
function modelFactory({ name = '', collection = '', fields = [], description = '', idFieldName = 'id', isSubmodel = false, }) {
    const _fields = {};
    fields.forEach(field => {
        if (!isSubmodel && field.name === idFieldName) {
            throw new Error(`Id field: ${idFieldName} is reservered cannot be included in fields.`);
        }
        _fields[field.name] = field;
    });
    // Create the id field and add it to the model field set.
    if (!isSubmodel) {
        _fields[idFieldName] = string_1.stringFieldFactory({
            name: idFieldName,
        });
    }
    /**
     * Retrievies a list of the model's field objects.
     */
    function getFields() {
        return Object.values(_fields);
    }
    /**
     * Retrieves a single model field by name.
     * @param field The name of the field to be retrieved.
     */
    function getField(field) {
        return _fields[field];
    }
    function _convertModelFormat(method, modelObj, fieldMask = null) {
        // Extract the field names to be used.
        const fieldNames = fieldMask !== null
            ? fieldMask
            : Object.keys(_fields);
        // Iterate over the field names, storing the transformed results into a new object.
        // Undefined and null values are excluded from the result.
        return fieldNames.reduce((result, fieldName) => {
            var _a, _b;
            // Check that the fieldName is included in the fields. If not, ignore it.
            if (!_fields[fieldName]) {
                return result;
            }
            const fromValue = (_a = modelObj) === null || _a === void 0 ? void 0 : _a[fieldName];
            const toValue = (_b = _fields[fieldName]) === null || _b === void 0 ? void 0 : _b[method](fromValue);
            // Undefined or null values are not included in the payload.
            if (toValue === undefined || toValue === null) {
                return result;
            }
            result[fieldName] = toValue;
            return result;
        }, {});
    }
    /**
     * Converts a record stored in the database to a ModelInstance.
     * @param record An object from the database
     * @param fieldMask An optional list of field names to create a partial instance.
     */
    function deserializeFromDb(record, fieldMask = null) {
        return _convertModelFormat('fromDb', record, fieldMask);
    }
    /**
     * Convert a Firestore Database record into a ModelInstance.
     * @param record The data object from the firestore document data.
     * @param idValue
     */
    function fromDb(idValue = '', record) {
        if (!isSubmodel && idValue) {
            record[idFieldName] = idValue;
        }
        const instance = deserializeFromDb(record);
        return instance;
    }
    /**
     * Serializes a ModelInstance to a DbRecord
     * @param instance The instance to be serialized
     * @param fieldMask An optinal list of field names that will create a partial record.
     */
    function serializeToDb(instance, fieldMask = null) {
        return _convertModelFormat('toDb', instance, fieldMask);
    }
    /**
     * Converts the fields of a `ModelInstance` into a `DbRecord`
     * @param instance
     */
    function toDb(instance) {
        const valuesToSerialize = Object.assign({}, instance);
        // Remove the id field, if it's present.
        if (!isSubmodel) {
            delete valuesToSerialize[idFieldName];
        }
        return serializeToDb(valuesToSerialize);
    }
    function deserializeFromApi(payload, fieldMask = null) {
        return _convertModelFormat('fromApi', payload, fieldMask);
    }
    /**
     * Creates a complete ModelInstance from an ApiPayload
     * @param payload
     */
    function fromApi(payload) {
        return deserializeFromApi(payload);
    }
    function serializeToApi(instance, fieldMask = null) {
        return _convertModelFormat('toApi', instance, fieldMask);
    }
    /**
     * Serializes a `ModelInstance` into a JSON serializable object.
     * @param instance
     */
    function toApi(instance) {
        return serializeToApi(instance);
    }
    /**
     * Validates a `ModelInstance`
     * @param instance
     * @param partial
     */
    function validate(instance) {
        // Ensure that all the keys in the instance are valid.
        Object.keys(instance).forEach(fieldName => {
            if (!_fields[fieldName]) {
                throw new errors_1.ValidationError(`Invalid field: ${fieldName}`);
            }
        });
        Object.values(_fields).forEach((field) => {
            const value = instance[field.name];
            if (value === undefined) {
                if (field.isRequired) {
                    throw new errors_1.ValidationError(`Field "${field.name}" is reqired.`);
                }
                return;
            }
            field.validate(value);
        });
    }
    /**
     * Creates a new model instance.
     * @param initialValues An optional map of initial field values.
     */
    function create(initialValues = {}) {
        const instance = Object.values(_fields).reduce((result, field) => {
            var _a;
            const value = (_a = initialValues[field.name]) !== null && _a !== void 0 ? _a : field.getDefault();
            // When values are undefined we want to exclude them from the payload.
            if (value !== undefined && value !== null) {
                result[field.name] = value;
            }
            return result;
        }, {});
        return instance;
    }
    /**
     * Extracts a partial model instance using a fieldMask
     * @param instance
     * @param fieldNames
     */
    function extractFields(instance, fieldMask) {
        return fieldMask.reduce((result, fieldName) => {
            var _a;
            const field = _fields[fieldName];
            if (!field) {
                throw new Error(`Invalid field name: ${fieldName}`);
            }
            const value = (_a = instance[field.name]) !== null && _a !== void 0 ? _a : field.getDefault();
            // When values are undefined we want to exclude them from the payload.
            if (value !== undefined && value !== null) {
                result[field.name] = value;
            }
            return result;
        }, {});
    }
    return {
        create,
        collection,
        description,
        deserializeFromApi,
        deserializeFromDb,
        extractFields,
        fromApi,
        fromDb,
        getField,
        getFields,
        name,
        serializeToApi,
        serializeToDb,
        toApi,
        toDb,
        validate,
        isSubmodel,
    };
}
exports.modelFactory = modelFactory;
//# sourceMappingURL=models.js.map